const VARIABLE_TOKEN_CAPTURE_REGEX = /\{\{([^}]+)\}\}/g;
const VARIABLE_TOKEN_REMOVE_REGEX = /\{\{[^}]+\}\}/g;
const VARIABLE_TOKEN_EDGE_REGEX = /^\{\{[^}]+\}\}|\{\{[^}]+\}\}$/;

export const TEMPLATE_NAME_MAX_LENGTH = 512;
export const HEADER_MAX_LENGTH = 60;
export const BODY_MAX_LENGTH = 1024;
export const BUTTON_LABEL_MAX_LENGTH = 25;
export const BUTTON_URL_MAX_LENGTH = 2000;
export const DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE = "pt_BR";
export const DEFAULT_WHATSAPP_TEMPLATE_CATEGORY = "MARKETING" as const;

type ValidationResult = { valid: true } | { valid: false; error: string };

export function normalizeTemplateName(rawName: string): string {
  return (rawName ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, TEMPLATE_NAME_MAX_LENGTH);
}

export function normalizeVariableTokens(text: string): string {
  if (!text) return "";
  return text.replace(/\{\{\s*([^}]*?)\s*\}\}/g, (_match, name) => {
    const normalizedVariable = String(name ?? "").trim();
    return `{{${normalizedVariable}}}`;
  });
}

export function extractUniqueNamedVariables(text: string): string[] {
  const set = new Set<string>();

  for (const match of text.matchAll(VARIABLE_TOKEN_CAPTURE_REGEX)) {
    const rawVarName = match[1];
    if (!rawVarName) continue;
    const varName = String(rawVarName).trim();
    if (varName) set.add(varName);
  }

  return [...set];
}

function validateVariableFormat(variableName: string): ValidationResult {
  if (/^\d+$/.test(variableName)) {
    return {
      valid: false,
      error: `Use variáveis nomeadas como "{{nome_variavel}}". Não use formato posicional "{{${variableName}}}".`,
    };
  }

  if (!/^[a-z0-9_]+$/.test(variableName)) {
    return {
      valid: false,
      error: `Nome de variável "${variableName}" inválido. Use apenas letras minúsculas, números e underscores.`,
    };
  }

  return { valid: true };
}

function validateVariablePlacement(text: string): ValidationResult {
  const lines = text.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const variableMatches = line.match(VARIABLE_TOKEN_REMOVE_REGEX);

    if (variableMatches && variableMatches.length > 0) {
      const textWithoutVars = line.replace(VARIABLE_TOKEN_REMOVE_REGEX, "").trim();
      if (textWithoutVars.length === 0 && line.length > 0) {
        return {
          valid: false,
          error: `Linha ${i + 1} contém apenas variáveis sem texto. Variáveis devem estar acompanhadas de texto descritivo.`,
        };
      }
    }
  }

  return { valid: true };
}

function validateVariablesInText(text: string): ValidationResult {
  if (!text) return { valid: true };

  const variableMatches = text.match(VARIABLE_TOKEN_REMOVE_REGEX);
  if (!variableMatches) return { valid: true };

  const variableNames = extractUniqueNamedVariables(text);
  for (const variableName of variableNames) {
    const validation = validateVariableFormat(variableName);
    if (!validation.valid) return validation;
  }

  return validateVariablePlacement(text);
}

export function validateTemplateName(name: string): ValidationResult {
  const normalizedName = normalizeTemplateName(name);

  if (!normalizedName) {
    return { valid: false, error: "Nome do template é obrigatório." };
  }

  if (normalizedName.length > TEMPLATE_NAME_MAX_LENGTH) {
    return {
      valid: false,
      error: `Nome do template deve ter no máximo ${TEMPLATE_NAME_MAX_LENGTH} caracteres.`,
    };
  }

  return { valid: true };
}

export function validateHeaderText(header: string): ValidationResult {
  if (!header) return { valid: true };
  if (header.length > HEADER_MAX_LENGTH) {
    return {
      valid: false,
      error: `Cabeçalho deve ter no máximo ${HEADER_MAX_LENGTH} caracteres.`,
    };
  }

  const variables = extractUniqueNamedVariables(header);
  if (variables.length > 1) {
    return {
      valid: false,
      error: "Não é possível adicionar mais de 1 variável no cabeçalho.",
    };
  }

  for (const variableName of variables) {
    const validation = validateVariableFormat(variableName);
    if (!validation.valid) return validation;
  }

  return { valid: true };
}

export function validateBodyText(body: string): ValidationResult {
  if (body.length > BODY_MAX_LENGTH) {
    return {
      valid: false,
      error: `Corpo deve ter no máximo ${BODY_MAX_LENGTH} caracteres.`,
    };
  }

  if (VARIABLE_TOKEN_EDGE_REGEX.test(body.trim())) {
    return {
      valid: false,
      error: "O corpo da mensagem não pode começar ou terminar com variável.",
    };
  }

  return validateVariablesInText(body);
}

export function validateButtonLabel(text: string): ValidationResult {
  const label = String(text ?? "").trim();

  if (!label) {
    return { valid: false, error: "Texto do botão é obrigatório." };
  }

  if (label.length > BUTTON_LABEL_MAX_LENGTH) {
    return {
      valid: false,
      error: `Texto do botão deve ter no máximo ${BUTTON_LABEL_MAX_LENGTH} caracteres.`,
    };
  }

  return { valid: true };
}

export function validateButtonUrl(url: string): ValidationResult {
  const normalizedUrl = String(url ?? "").trim();

  if (!normalizedUrl) {
    return { valid: false, error: "URL do botão é obrigatória." };
  }

  if (normalizedUrl.length > BUTTON_URL_MAX_LENGTH) {
    return {
      valid: false,
      error: `URL do botão deve ter no máximo ${BUTTON_URL_MAX_LENGTH} caracteres.`,
    };
  }

  const isHttpsUrl = normalizedUrl.startsWith("https://");
  const isLocalhostHttpUrl = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(
    normalizedUrl,
  );

  if (!isHttpsUrl && !isLocalhostHttpUrl) {
    return {
      valid: false,
      error:
        "A URL do botão deve começar com https:// (ou http://localhost em ambiente local).",
    };
  }

  const variables = extractUniqueNamedVariables(normalizedUrl);
  if (variables.length > 1) {
    return {
      valid: false,
      error: "A URL do botão permite no máximo 1 variável.",
    };
  }

  if (variables.length === 1 && !/\{\{[^}]+\}\}$/.test(normalizedUrl)) {
    return {
      valid: false,
      error: "Variável na URL deve estar apenas no final.",
    };
  }

  for (const variableName of variables) {
    const validation = validateVariableFormat(variableName);
    if (!validation.valid) return validation;
  }

  return { valid: true };
}

export function normalizeTemplateInput(input: {
  name: string;
  header?: string;
  body: string;
}): { name: string; header: string; body: string } {
  return {
    name: normalizeTemplateName(input.name),
    header: normalizeVariableTokens((input.header ?? "").trim()),
    body: normalizeVariableTokens(input.body ?? ""),
  };
}
